import * as React from 'react'
import isEmpty from 'lodash.isempty'
import { Button, Icon, InputGroup } from '@blueprintjs/core'
import { useState } from 'react'
import RichEditor from '../../components/editors/Rich'
import { Renderer } from '../admin/templates/builder/Form'

const containerConfig = { cols: 1 }

export const DescriptionOrAttributes: React.FC<{
  description: string
  setDescription: React.Dispatch<React.SetStateAction<string>>
  loaded: boolean
  templateForm: unknown[]
  templateLayout: unknown[]
  onTemplateFormChange: (index, value) => void
  onTemplateFormChangeCombobox: (index, input: Record<string, unknown>) => void
  categorySelected: boolean
  strategySelected: boolean
  templateId: number
}> = props => {
  const [attributesTabSelected, setAttributesTabSelected] = useState(true)
  const mayToggleAttributesTab = !!props.templateForm
  const showSkeleton = !props.categorySelected || !props.strategySelected

  let attributesTabActive = attributesTabSelected
  // when changing between categories with a different number of template items, there may be a brief render where these don't align, which crashes Renderer
  if (props.templateForm && props.templateLayout && props.templateForm.length !== props.templateLayout?.length) {
    attributesTabActive = false
  }
  if (!props.templateForm) {
    // when there is no template, we need to allow them to just enter a description themselves. Helps with changing between categories.
    attributesTabActive = false
  }
  if (showSkeleton) {
    attributesTabActive = true // we'll show a skeleton version of attributes
  }
  if (!props.loaded) {
    attributesTabActive = true // minimize flicker when loading takes 2 API calls
  }

  const toggleTab = () => {
    setAttributesTabSelected(v => !v)
  }

  const skeletonRenderer = (
    <div className="skeleton-renderer">
      <InputGroup />
      <InputGroup />
      <InputGroup />
      <InputGroup />
    </div>
  )

  const realRenderer = (
    <Renderer
      previewMode
      form={props.templateForm}
      onFormPreviewChange={props.onTemplateFormChange}
      onInputChange={props.onTemplateFormChangeCombobox}
      container={containerConfig}
      layout={props.templateLayout}
      onConfigureInput={undefined}
      onLayoutChange={undefined}
      onDuplicateInputClick={undefined}
      requiredLabelIconName="warning-sign"
      templateId={props.templateId}
    />
  )

  const ditherOverlay = (
    <>
      <div className="dither-overlay" />
      <div className="message-over-dither">
        <p>Select category and listing strategy</p>
      </div>
    </>
  )

  const header = attributesTabActive ? (
    <h1 className="maybe-warning-icon">PRODUCT ATTRIBUTES</h1>
  ) : (
    <>
      <h1 className="maybe-warning-icon">DESCRIPTION</h1>
      {isEmpty(props.description) && <Icon icon="warning-sign" color="gray" />}
    </>
  )

  let bottomComponent = null
  if (showSkeleton) {
    bottomComponent = skeletonRenderer
  } else if (attributesTabActive) {
    if (!props.loaded) {
      bottomComponent = <div className="bp3-skeleton">{skeletonRenderer}</div>
    } else {
      bottomComponent = realRenderer
    }
  } else {
    bottomComponent = (
      <div data-testid="description-editor" className={!props.loaded ? 'bp3-skeleton' : undefined}>
        <RichEditor
          showTemplates
          showTemplatesPosition="bottom"
          value={props.description}
          // 600 px fills up most of a standard 23" monitor.
          // Users can make this taller, but we lose the height if they toggle between attributes and description
          editorHeight={600}
          onChange={v => props.setDescription(v)}
        />
        {/* TipTap renders directly in the DOM, no iframe - RTL can see it natively */}
      </div>
    )
    // If this branch renders for a very brief time, it causes tiny-mce to silently crash on page load.
    // Be careful if you want to consume "props.loading", which is initially set to false for a very brief time.
  }

  return (
    <div style={{ position: 'relative', zIndex: 0 }}>
      {showSkeleton && ditherOverlay}

      {header}

      <Button
        className="align-button-font-baseline-with-h1"
        minimal
        intent="primary"
        style={{ float: 'right' }}
        disabled={!props.loaded || !mayToggleAttributesTab}
        onClick={toggleTab}
      >
        {attributesTabActive ? 'View Description' : 'View Attributes'}
      </Button>
      <div style={{ clear: 'both' }} />

      {bottomComponent}
    </div>
  )
}
